import {
  createTaskEditorBlock,
  migrateTaskEditorStateFromLegacy,
  TaskEditorBlock,
  TaskEditorState,
} from "./taskEditorUtils";

export const cloneTaskEditorState = (
  state: TaskEditorState | unknown
): TaskEditorState => migrateTaskEditorStateFromLegacy(state);

export const createEmptyTaskEditorState = (): TaskEditorState => ({
  task_id: "",
  title: "",
  blocks: [createTaskEditorBlock()],
});

// ---------------------------------------------------------------------------
// 동기화 / 3-way merge — BE `app/utils/revision.py` 와 비트 동일한 알고리즘.
// SubtleCrypto 가 async 라 React state 갱신 경로에 부담을 주므로, 동기 가능한
// FNV-1a 64bit + canonical JSON 위에서 16-hex content-hash 를 만든다.
// ---------------------------------------------------------------------------

// target es5 에서 BigInt 리터럴(`Nn`) 이 막혀 BigInt() 생성자로 표기.
const FNV_OFFSET_64 = BigInt("0xcbf29ce484222325");
const FNV_PRIME_64 = BigInt("0x100000001b3");
const MASK_64 = BigInt("0xffffffffffffffff");

function fnv1a64(bytes: Uint8Array): bigint {
  let h = FNV_OFFSET_64;
  for (let i = 0; i < bytes.length; i++) {
    h = (h ^ BigInt(bytes[i])) & MASK_64;
    h = (h * FNV_PRIME_64) & MASK_64;
  }
  return h;
}

function canonicalJSON(value: unknown): string {
  // 객체 key 재귀 정렬 + 공백 제거. 한글은 그대로 (ensure_ascii=False 와 동일).
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return "[" + value.map((v) => canonicalJSON(v)).join(",") + "]";
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts = keys.map(
    (k) => JSON.stringify(k) + ":" + canonicalJSON(obj[k])
  );
  return "{" + parts.join(",") + "}";
}

// TextEncoder 는 jest jsdom 에 없고 polyfill 추가도 부담이라 UTF-8 을 손으로 인코딩.
// 한글은 모두 BMP (3 byte) 라 surrogate pair 처리는 거의 안 타지만, ES6 surrogate
// (이모지 등) 까지 안전하게 다루기 위해 codePoint 기반으로 작성.
function utf8Bytes(text: string): Uint8Array {
  const out: number[] = [];
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (cp < 0x80) {
      out.push(cp);
    } else if (cp < 0x800) {
      out.push(0xc0 | (cp >> 6));
      out.push(0x80 | (cp & 0x3f));
    } else if (cp < 0x10000) {
      out.push(0xe0 | (cp >> 12));
      out.push(0x80 | ((cp >> 6) & 0x3f));
      out.push(0x80 | (cp & 0x3f));
    } else {
      out.push(0xf0 | (cp >> 18));
      out.push(0x80 | ((cp >> 12) & 0x3f));
      out.push(0x80 | ((cp >> 6) & 0x3f));
      out.push(0x80 | (cp & 0x3f));
    }
  }
  return Uint8Array.from(out);
}

export function stableHashBlocks(blocks: TaskEditorBlock[] | null): string {
  const canonical = canonicalJSON(blocks ?? []);
  const bytes = utf8Bytes(canonical);
  const digest = fnv1a64(bytes);
  return digest.toString(16).padStart(16, "0");
}

function blockEquals(a: TaskEditorBlock, b: TaskEditorBlock): boolean {
  return canonicalJSON(a) === canonicalJSON(b);
}

/**
 * base 와 next 의 blocks 를 index 기준으로 비교해 다른 위치의 index set 을 반환.
 * 길이가 다르면 양쪽 끝까지의 부분집합만 비교 (호출자가 length 불일치를 별도 판단).
 */
export function diffBlocksByIndex(
  base: TaskEditorBlock[],
  next: TaskEditorBlock[]
): Set<number> {
  const changed = new Set<number>();
  const len = Math.min(base.length, next.length);
  for (let i = 0; i < len; i++) {
    if (!blockEquals(base[i], next[i])) changed.add(i);
  }
  return changed;
}

export type ThreeWayMergeResult =
  | { kind: "applied"; blocks: TaskEditorBlock[] }
  | { kind: "auto_merged"; blocks: TaskEditorBlock[] }
  | {
      kind: "conflict";
      reason: "overlap" | "length_mismatch";
      conflictBlockIndexes: number[];
      agentBlocks: TaskEditorBlock[];
    };

/**
 * base / ours(=user 현재 편집) / theirs(=agent patch) 의 3-way merge.
 *
 * - ours == base (사용자가 턴 도중 안 건드림): theirs 그대로 적용 → "applied"
 * - 길이 동일 + index 충돌 없음: index 마다 user 우선 (touched) 또는 agent 우선 → "auto_merged"
 * - 길이 동일 + 충돌: "conflict" (overlap) — conflictBlockIndexes 반환
 * - 길이 불일치: "conflict" (length_mismatch) — v1 은 전체 충돌로 escalate
 */
export function threeWayMergeBlocks(
  base: TaskEditorBlock[],
  ours: TaskEditorBlock[],
  theirs: TaskEditorBlock[]
): ThreeWayMergeResult {
  const userDiff = diffBlocksByIndex(base, ours);
  if (userDiff.size === 0 && base.length === ours.length) {
    return { kind: "applied", blocks: theirs };
  }

  if (ours.length !== theirs.length) {
    return {
      kind: "conflict",
      reason: "length_mismatch",
      conflictBlockIndexes: [],
      agentBlocks: theirs,
    };
  }

  const agentDiff = diffBlocksByIndex(base, theirs);
  const overlap: number[] = [];
  // target es5 + downlevelIteration off → Set.forEach 사용.
  userDiff.forEach((idx) => {
    if (agentDiff.has(idx)) overlap.push(idx);
  });
  if (overlap.length > 0) {
    overlap.sort((a, b) => a - b);
    return {
      kind: "conflict",
      reason: "overlap",
      conflictBlockIndexes: overlap,
      agentBlocks: theirs,
    };
  }

  const merged = theirs.map((agentBlock, idx) =>
    userDiff.has(idx) ? ours[idx] : agentBlock
  );
  return { kind: "auto_merged", blocks: merged };
}
